import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter, map, Observable } from 'rxjs';

import { UserService } from './user/user.service';
import { SsoRedirectDialogService } from './sso-redirect-dialog.service';
import { FaqDialogComponent } from '../components/faq-dialog/faq-dialog.component';

// Ação disparada ao abrir o card/app: SSO (redirect), rota interna ou modal de FAQ.
export type AppKind = 'sso' | 'route' | 'faq';

export interface AppCard {
  title: string;
  trigram: string;
  description: string;
  icon: string;
  // Sigla exibida no ícone do card; ausência = derivada do título (ver abbreviationOf).
  abbreviation?: string;
  // Tokens de trigram aceitos no appPermissions (o back varia o prefixo: EB1-DRE, DRE-INDUS).
  permissionKeys?: string[];
  // App liberado para todos, sem depender de appPermissions.
  alwaysVisible?: boolean;
  // Tipo de navegação; ausência = 'sso'.
  kind?: AppKind;
  // Rota interna quando kind === 'route'.
  route?: string;
}

// Fonte única da lista de aplicações do portal, reaproveitada pela Home e pela sidebar.
@Injectable({ providedIn: 'root' })
export class AppsService {
  constructor(
    private readonly userService: UserService,
    private readonly ssoRedirectDialog: SsoRedirectDialogService,
    private readonly router: Router,
    private readonly dialog: MatDialog
  ) {}

  readonly apps: AppCard[] = [
    {
      title: 'DRE',
      trigram: 'dre',
      description: 'Demonstrativo de Resultado do Exercício',
      icon: 'analytics',
      permissionKeys: ['DRE']
    },
    {
      title: 'UD',
      trigram: 'tld',
      description: 'Programa UD',
      icon: 'tire_repair',
      permissionKeys: ['TLD']
    },
    {
      title: 'Jornada Acompanhada',
      trigram: 'joa',
      description: 'Voucher',
      icon: 'route',
      permissionKeys: ['VOUCHER', 'JOA']
    },
    // Bibnet, RME e PAT ainda não estão cadastrados no back: liberados para todos até existir trigram.
    {
      title: 'Bibnet',
      trigram: 'bib',
      description: 'Portal de Informações Bibnet',
      icon: 'hub',
      permissionKeys: ['API']
    },
    {
      title: 'RME',
      trigram: 'rme',
      description: 'Requisição de Materiais e Equipamentos',
      icon: 'inventory_2'
    },
    {
      title: 'PAT',
      trigram: 'pw',
      description: 'Pedido de Assistência Técnica',
      icon: 'build'
    },
    {
      title: 'FAQ',
      trigram: 'faq',
      description: 'Perguntas Frequentes e Ajuda',
      icon: 'quiz',
      alwaysVisible: true,
      kind: 'faq'
    },
    {
      title: 'Fale com a Michelin',
      trigram: 'fal',
      description: 'Como podemos ajudar você?',
      icon: 'forum',
      alwaysVisible: true,
      kind: 'route',
      route: '/contact-us'
    }
  ];

  // Título do app mantido sempre no fim da lista quando não é favorito.
  static readonly PINNED_LAST_TITLE = 'Contact us';

  // Siglas fixadas pelo negócio (chave: título normalizado).
  private static readonly ABBREVIATION_OVERRIDES: Record<string, string> = {
    'programa ud': 'UD',
    avatar: 'UD',
    'programa de excelencia michelin': 'PEM'
  };

  private static readonly ABBREVIATION_STOPWORDS = new Set(['a', 'as', 'o', 'os', 'de', 'da', 'das', 'do', 'dos', 'e', 'com', 'para']);

  abbreviationOf(app: AppCard): string {
    if (app.abbreviation) {
      return app.abbreviation;
    }
    const normalized = app.title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
    const override = AppsService.ABBREVIATION_OVERRIDES[normalized];
    if (override) {
      return override;
    }
    const words = normalized.split(/\s+/).filter(word => !AppsService.ABBREVIATION_STOPWORDS.has(word));
    if (words.length > 1) {
      return words.map(word => word[0]).join('').toUpperCase().slice(0, 4);
    }
    return app.title.length <= 4 ? app.title.toUpperCase() : app.trigram.toUpperCase();
  }

  getVisibleApps(): Observable<AppCard[]> {
    return this.userService.getMe().pipe(
      map(me => this.filterByPermissions(Array.isArray(me?.appPermissions) ? me.appPermissions : []))
    );
  }

  filterByPermissions(permissions: string[]): AppCard[] {
    const granted = this.grantedKeys(permissions);
    return this.apps.filter(
      app => app.alwaysVisible || !!app.permissionKeys?.some(key => granted.has(key.toUpperCase()))
    );
  }

  // Contact us vai para o fim da lista, exceto quando marcado como favorito.
  orderWithPinnedLast(apps: AppCard[], isFavorite: (app: AppCard) => boolean): AppCard[] {
    const pinned = apps.filter(app => app.title === AppsService.PINNED_LAST_TITLE && !isFavorite(app));
    const rest = apps.filter(app => !pinned.includes(app));
    return [...rest, ...pinned];
  }

  // Dispara a ação do app conforme o tipo: rota interna, modal de FAQ ou redirect SSO.
  open(app: AppCard): void {
    if (app.kind === 'route' && app.route) {
      this.router.navigate([app.route]);
      return;
    }
    if (app.kind === 'faq') {
      this.openFaq();
      return;
    }
    this.ssoRedirectDialog.confirm(app.title, app.trigram);
  }

  openFaq(): void {
    const ref = this.dialog.open(FaqDialogComponent, {
      width: '560px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'faq-dialog-panel',
      autoFocus: false,
      // Clique/toque fora não fecha: evita perder a pesquisa ao tocar sem querer; só X, "Fale com a Michelin" ou Esc.
      disableClose: true
    });
    ref.keydownEvents().pipe(filter(event => event.key === 'Escape')).subscribe(() => ref.close());
  }

  // O trigram vem com prefixo/sufixo de plataforma variável (EB1-DRE, DRE-INDUS),
  // então o match é por token exato e não pela string inteira.
  private grantedKeys(permissions: string[]): Set<string> {
    const keys = new Set<string>();
    for (const permission of permissions) {
      for (const token of String(permission).toUpperCase().split(/[-_\s./]+/)) {
        if (token) {
          keys.add(token);
        }
      }
    }
    return keys;
  }
}
